<script setup>
import { DateInput } from '@addictivegroup/addictive-datetime'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatDate } from '@/lib/format.js'
import UiModal from '@/components/ui/UiModal.vue'
import UiButton from '@/components/ui/UiButton.vue'
import UiField from '@/components/ui/UiField.vue'
import SearchPicker from '@/components/form/SearchPicker.vue'

/**
 * What starts a service (D-176), set in one place and confirmed: the acceptance of stages (any of
 * the contract's, accepted ones too), a date, or by hand; and from when it is billed. Before it is
 * confirmed, what will follow is said: a service waiting only for accepted stages starts at once,
 * a date past starts it tonight, a service started by hand never starts by itself.
 */
const props = defineProps({
  activation: { type: Object, default: null },
  stages: { type: Array, default: () => [] },
  busy: Boolean,
})
const emit = defineEmits(['close', 'save'])
const { t } = useI18n()
const today = new Date().toISOString().slice(0, 10)
const form = ref({})
watch(() => props.activation, (x) => {
  if (x) form.value = { trigger: x.trigger, trigger_stages: [...(x.trigger_stages || [])], start_date: x.start_date || '', billing_shift: x.billing_shift, billing_shift_days: x.billing_shift_days ?? '' }
}, { immediate: true })
const pending = computed(() => props.activation?.status === 'pending')
const byUuid = computed(() => new Map(props.stages.map((s) => [s.uuid, s])))
const options = computed(() => props.stages.map((s) => ({
  value: s.uuid, label: `${s.agreement} · ${s.name}`,
  hint: s.accepted_on ? t('contract.acceptedOn', { date: formatDate(s.accepted_on) }) : s.planned_end ? t('contract.plannedAcceptance', { date: formatDate(s.planned_end) }) : t(`contract.stageStatus.${s.status}`),
})))

// The day billing would start for a start on the day given, with the shift chosen.
function billingFrom(day) {
  if (!day) return null
  const [y, m, d] = day.split('-').map(Number)
  if (form.value.billing_shift === 'next_month') return d === 1 ? day : m === 12 ? `${y + 1}-01-01` : `${y}-${String(m + 1).padStart(2, '0')}-01`
  if (form.value.billing_shift === 'days') return new Date(Date.UTC(y, m - 1, d) + Number(form.value.billing_shift_days || 0) * 86400000).toISOString().slice(0, 10)
  return day
}

// What will follow, said before it is confirmed.
const consequences = computed(() => {
  const f = form.value
  const out = []
  if (!pending.value) {
    // A running service: only its billing can change.
    const start = props.activation?.started_on
    out.push({ tone: 'info', text: t('contract.rule.running', { date: formatDate(start), billing: formatDate(billingFrom(start)) }) })
    return out
  }
  if (f.trigger === 'stage') {
    const chosen = f.trigger_stages.map((u) => byUuid.value.get(u)).filter(Boolean)
    if (!chosen.length) out.push({ tone: 'warn', text: t('contract.rule.noStages') })
    else {
      const open = chosen.filter((s) => s.status !== 'accepted')
      if (!open.length) {
        const last = chosen.map((s) => s.accepted_on).sort().pop()
        out.push({ tone: 'warn', text: t('contract.rule.startsNow', { date: formatDate(last), billing: formatDate(billingFrom(last)) }) })
      } else {
        const dates = chosen.map((s) => s.accepted_on || s.planned_end)
        const plan = dates.every(Boolean) ? dates.sort().pop() : null
        out.push({ tone: 'info', text: t('contract.rule.waits', { stages: open.map((s) => `${s.agreement} · ${s.name}`).join(', ') }) })
        out.push({ tone: 'info', text: plan ? t('contract.rule.planned', { date: formatDate(plan), billing: formatDate(billingFrom(plan)) }) : t('contract.rule.noPlan') })
      }
      const accepted = chosen.filter((s) => s.status === 'accepted')
      if (accepted.length && open.length) out.push({ tone: 'info', text: t('contract.rule.someAccepted', { stages: accepted.map((s) => s.name).join(', ') }) })
    }
  } else if (f.trigger === 'date') {
    if (!f.start_date) out.push({ tone: 'warn', text: t('contract.rule.noDate') })
    else if (f.start_date <= today) out.push({ tone: 'warn', text: t('contract.rule.datePast', { date: formatDate(f.start_date), billing: formatDate(billingFrom(f.start_date)) }) })
    else out.push({ tone: 'info', text: t('contract.rule.dateFuture', { date: formatDate(f.start_date), billing: formatDate(billingFrom(f.start_date)) }) })
  } else {
    out.push({ tone: 'info', text: t('contract.rule.manual') })
  }
  return out
})
const changed = computed(() => {
  const x = props.activation
  if (!x) return false
  const f = form.value
  return f.trigger !== x.trigger || JSON.stringify([...f.trigger_stages].sort()) !== JSON.stringify([...(x.trigger_stages || [])].sort()) || (f.start_date || null) !== (x.start_date || null)
    || f.billing_shift !== x.billing_shift || Number(f.billing_shift_days || 0) !== Number(x.billing_shift_days || 0)
})
function save() {
  const f = form.value
  emit('save', pending.value
    ? { trigger: f.trigger, trigger_stages: f.trigger === 'stage' ? f.trigger_stages : (props.activation.trigger_stages || []), start_date: f.trigger === 'date' ? f.start_date || null : null,
        billing_shift: f.billing_shift, billing_shift_days: f.billing_shift === 'days' ? Number(f.billing_shift_days || 0) : null }
    : { billing_shift: f.billing_shift, billing_shift_days: f.billing_shift === 'days' ? Number(f.billing_shift_days || 0) : null })
}
</script>

<template>
  <UiModal :open="!!activation" :title="$t('contract.rule.title', { name: activation?.name || '' })" size="lg" @close="emit('close')">
    <div v-if="activation" class="space-y-4" data-testid="activation-rule">
      <fieldset v-if="pending" class="space-y-2">
        <legend class="label">{{ $t('contract.trigger') }}</legend>
        <label v-for="tr in ['stage', 'date', 'manual']" :key="tr" class="flex items-start gap-2 text-sm">
          <input v-model="form.trigger" type="radio" :value="tr" class="mt-0.5 accent-brand-600" :data-testid="`rule-trigger-${tr}`" />
          <span>{{ $t(`contract.triggers.${tr}`) }}<span class="block text-xs text-muted">{{ $t(`contract.rule.hint_${tr}`) }}</span></span>
        </label>
      </fieldset>
      <UiField v-if="pending && form.trigger === 'stage'" :label="$t('contract.waitsFor')">
        <SearchPicker v-model="form.trigger_stages" :options="options" :placeholder="$t('contract.waitsSearch')" />
      </UiField>
      <UiField v-if="pending && form.trigger === 'date'" :label="$t('contract.startDate')"><DateInput v-model="form.start_date" class="w-48" direction="future" data-testid="rule-date" /></UiField>
      <div class="grid gap-3 sm:grid-cols-2">
        <UiField :label="$t('contract.billing')">
          <select v-model="form.billing_shift" class="input" data-testid="rule-billing"><option v-for="b in ['none', 'next_month', 'days']" :key="b" :value="b">{{ $t(`contract.shifts.${b}`) }}</option></select>
        </UiField>
        <UiField v-if="form.billing_shift === 'days'" :label="$t('contract.rule.days')"><input v-model="form.billing_shift_days" type="number" min="0" class="input" /></UiField>
      </div>
      <section class="rounded-xl border border-default bg-[var(--surface-2)] p-3" data-testid="rule-consequences">
        <p class="label mb-1">{{ $t('contract.rule.whatHappens') }}</p>
        <ul class="space-y-1 text-sm">
          <li v-for="(c, i) in consequences" :key="i" :class="c.tone === 'warn' ? 'text-amber-800 dark:text-amber-200' : ''">
            <i :class="['fa-solid fa-fw', c.tone === 'warn' ? 'fa-triangle-exclamation' : 'fa-circle-info text-faint']" aria-hidden="true"></i> {{ c.text }}
          </li>
        </ul>
      </section>
    </div>
    <template #footer>
      <UiButton @click="emit('close')">{{ $t('common.cancel') }}</UiButton>
      <UiButton variant="primary" icon="fa-check" :loading="busy" :disabled="!changed" data-testid="rule-confirm" @click="save">{{ $t('contract.rule.confirm') }}</UiButton>
    </template>
  </UiModal>
</template>
